import { GeneratorSideModel } from "./GeneratorSideModel";
import { ObservableModelNode } from "./observable";
import { SPECS_DISPLAY_METHODS } from "./displayMethods";
import type { SpecsCatalogFabric, SpecsCatalogFabricColor } from "./types";
import { SpecsFabricModel } from "./fabrics/SpecsFabricModel";
import {
  cloneValue,
  expectObject,
  readBoolean,
  readObjectArray,
  readOptionalNumber,
  readString,
  readStringEnum,
  readStringArray,
  splitKnown,
} from "./utils";
import type { JsonObject } from "./utils";
import type { SpecsDisplayMethod } from "../types";

export type LayerCompositeFill = {
  baseColor: string;
  baseTextureUrl: string | null;
  skipFill: boolean;
};

export class LayerModel extends ObservableModelNode {
  static readonly INHERITED_COLOR_NAME = "inherited";
  static readonly FALLBACK_PREVIEW_COLOR = "#4a7cdc";

  static isInheritedColor(color: string | null | undefined) {
    return color?.toLowerCase() === LayerModel.INHERITED_COLOR_NAME;
  }

  static getFabrics(
    fabrics: SpecsCatalogFabric[],
    layer: { fabricNames: string[] }
  ) {
    return layer.fabricNames
      .map((fabricName) =>
        fabrics.find((candidate) => candidate.name === fabricName)
      )
      .filter((fabric): fabric is SpecsCatalogFabric => !!fabric);
  }

  static shouldSkipInheritedFill(
    layer: LayerModel,
    color: SpecsCatalogFabricColor | null,
    sideName: string
  ) {
    if (!color) {
      return false;
    }

    if (color.migrateTo) {
      return false;
    }

    const side = layer.getSide(sideName);
    return !!side && !side.alwaysRedraw;
  }

  static resolveCompositeFill(args: {
    layer: LayerModel;
    baseColor: string;
    previewColor: string;
    fabrics: SpecsCatalogFabric[];
    sideName: string;
  }): LayerCompositeFill {
    const inherited = LayerModel.isInheritedColor(args.baseColor);
    const layerFabrics = LayerModel.getFabrics(args.fabrics, args.layer);
    let directColor: SpecsCatalogFabricColor | null = null;
    let matchingColor: SpecsCatalogFabricColor | null = null;

    if (inherited) {
      const sourceColor = SpecsFabricModel.findColorByStoredValue(
        args.fabrics,
        args.previewColor
      );
      directColor = SpecsFabricModel.findColorByName(
        layerFabrics,
        sourceColor?.name
      );
      matchingColor =
        directColor ??
        SpecsFabricModel.findColorByName(layerFabrics, sourceColor?.fallback);
    } else {
      matchingColor = SpecsFabricModel.findColorByStoredValue(
        layerFabrics,
        args.previewColor
      );
    }

    return {
      baseColor: matchingColor
        ? SpecsFabricModel.getColorValue(matchingColor) ||
          LayerModel.FALLBACK_PREVIEW_COLOR
        : args.previewColor,
      baseTextureUrl: matchingColor?.textureUrl ?? null,
      skipFill:
        inherited &&
        LayerModel.shouldSkipInheritedFill(
          args.layer,
          directColor,
          args.sideName
        ),
    };
  }

  canInherit(element: { inherit?: boolean } | null | undefined) {
    return !!element?.inherit && !this.excludeFromInheritance;
  }

  getDefaultPreviewColor(fabrics: SpecsCatalogFabric[]) {
    for (const name of this.fabricNames) {
      const fabric = fabrics.find((candidate) => candidate.name === name);
      if (fabric?.defaultColor?.generatorHex || fabric?.defaultColor?.hex) {
        return fabric.defaultColor.generatorHex ?? fabric.defaultColor.hex!;
      }
    }

    return LayerModel.FALLBACK_PREVIEW_COLOR;
  }

  name!: string;
  layerLabel!: string;
  fabricNames!: string[];
  required!: boolean;
  credits!: number;
  parentOfInheritanceGroup!: string;
  excludeFromInheritance!: boolean;
  excludeFromColorCount!: boolean;
  immuneToFabricOverwriteByDecoration!: boolean;
  excludeFromColorBleed!: boolean;
  lineHighlightColor!: string;
  displayable!: boolean;
  displayGroup!: string;
  displayMethod!: SpecsDisplayMethod;
  sides!: GeneratorSideModel[];
  extra!: JsonObject;
  displayExtra!: JsonObject;
  generatorExtra!: JsonObject;
  private hasExcludeFromColorCount = false;
  private hasImmuneToFabricOverwriteByDecoration = false;
  private hasExcludeFromColorBleed = false;
  private hasDisplayMethod = false;

  constructor() {
    super();
    this.defineObservableValue("name", "");
    this.defineObservableValue("layerLabel", "");
    this.defineObservableValue("fabricNames", []);
    this.defineObservableValue("required", true);
    this.defineObservableValue("credits", 0);
    this.defineObservableValue("parentOfInheritanceGroup", "");
    this.defineObservableValue("excludeFromInheritance", false);
    this.defineObservableValue("excludeFromColorCount", false);
    this.defineObservableValue("immuneToFabricOverwriteByDecoration", false);
    this.defineObservableValue("excludeFromColorBleed", false);
    this.defineObservableValue("lineHighlightColor", "");
    this.defineObservableValue("displayable", true);
    this.defineObservableValue("displayGroup", "main");
    this.defineObservableValue("displayMethod", "colorPalette");
    this.defineObservableValue("sides", []);
    this.defineObservableValue("extra", {});
    this.defineObservableValue("displayExtra", {});
    this.defineObservableValue("generatorExtra", {});
  }

  restore(data: unknown) {
    const source = expectObject(data, "layer");
    const display = source.display
      ? expectObject(source.display, "layer.display")
      : {};
    const generator = source.generator
      ? expectObject(source.generator, "layer.generator")
      : {};

    this.hasExcludeFromColorCount = "excludeFromColorCount" in source;
    this.hasImmuneToFabricOverwriteByDecoration =
      "immuneToFabricOverwriteByDecoration" in source;
    this.hasExcludeFromColorBleed = "excludeFromColorBleed" in source;
    this.hasDisplayMethod = "displayMethod" in display;
    this.extra = splitKnown(source, [
      "name",
      "layerLabel",
      "fabricNames",
      "required",
      "credits",
      "parentOfInheritanceGroup",
      "excludeFromInheritance",
      "excludeFromColorCount",
      "immuneToFabricOverwriteByDecoration",
      "excludeFromColorBleed",
      "lineHighlightColor",
      "display",
      "generator",
    ]);
    this.displayExtra = splitKnown(display, [
      "displayable",
      "group",
      "displayMethod",
    ]);
    this.generatorExtra = splitKnown(generator, ["sides"]);
    this.name = readString(source, "name");
    this.layerLabel = readString(source, "layerLabel");
    this.fabricNames = cloneValue(readStringArray(source, "fabricNames"));
    this.required = readBoolean(source, "required", true);
    this.credits = readOptionalNumber(source, "credits") ?? 0;
    this.parentOfInheritanceGroup = readString(
      source,
      "parentOfInheritanceGroup"
    );
    this.excludeFromInheritance = readBoolean(
      source,
      "excludeFromInheritance",
      false
    );
    this.excludeFromColorCount = readBoolean(
      source,
      "excludeFromColorCount",
      false
    );
    this.immuneToFabricOverwriteByDecoration = readBoolean(
      source,
      "immuneToFabricOverwriteByDecoration",
      false
    );
    this.excludeFromColorBleed = readBoolean(
      source,
      "excludeFromColorBleed",
      false
    );
    this.lineHighlightColor = readString(source, "lineHighlightColor");
    this.displayable = readBoolean(display, "displayable", true);
    this.displayGroup = readString(display, "group", "main");
    this.displayMethod = readStringEnum(
      display,
      "displayMethod",
      SPECS_DISPLAY_METHODS,
      "colorPalette"
    );
    this.sides = readObjectArray(generator, "sides").map((side) =>
      new GeneratorSideModel().restore(side)
    );

    return this;
  }

  save() {
    return {
      ...cloneValue(this.extra),
      name: this.name,
      layerLabel: this.layerLabel,
      fabricNames: cloneValue(this.fabricNames),
      required: this.required,
      credits: this.credits,
      ...(this.parentOfInheritanceGroup
        ? { parentOfInheritanceGroup: this.parentOfInheritanceGroup }
        : {}),
      ...(this.excludeFromInheritance
        ? { excludeFromInheritance: this.excludeFromInheritance }
        : {}),
      ...(this.hasExcludeFromColorCount || this.excludeFromColorCount
        ? { excludeFromColorCount: this.excludeFromColorCount }
        : {}),
      ...(this.hasImmuneToFabricOverwriteByDecoration ||
      this.immuneToFabricOverwriteByDecoration
        ? {
            immuneToFabricOverwriteByDecoration:
              this.immuneToFabricOverwriteByDecoration,
          }
        : {}),
      ...(this.hasExcludeFromColorBleed || this.excludeFromColorBleed
        ? { excludeFromColorBleed: this.excludeFromColorBleed }
        : {}),
      ...(this.lineHighlightColor
        ? { lineHighlightColor: this.lineHighlightColor }
        : {}),
      generator: {
        ...cloneValue(this.generatorExtra),
        sides: this.sides.map((side) => side.save()),
      },
      display: {
        ...cloneValue(this.displayExtra),
        displayable: this.displayable,
        group: this.displayGroup,
        ...(this.hasDisplayMethod || this.displayMethod !== "colorPalette"
          ? { displayMethod: this.displayMethod }
          : {}),
      },
    };
  }

  getSide(sideName: string) {
    return this.sides.find((side) => side.name === sideName) ?? null;
  }

  ensureSide(sideName: string) {
    const existing = this.getSide(sideName);
    if (existing) {
      return existing;
    }

    const template = this.sides[0] ?? null;
    const side = new GeneratorSideModel();
    side.name = sideName;
    side.zIndex = template?.zIndex ?? null;
    side.zIndexHighlight = template?.zIndexHighlight ?? null;
    side.imageUrl = null;
    side.highlightUrl = null;
    side.alwaysRedraw = false;
    side.extra = {};
    this.sides.push(side);
    return side;
  }

  setFabricNames(fabricNames: string[]) {
    this.fabricNames = cloneValue(fabricNames.filter(Boolean));
    return true;
  }

  setInheritanceMetadata(
    inheritanceMetadata: {
      parentOfInheritanceGroup?: string;
      excludeFromInheritance?: boolean;
    },
    options: { inheritanceGroups?: string[] } = {}
  ) {
    if (
      inheritanceMetadata.parentOfInheritanceGroup !== undefined &&
      inheritanceMetadata.parentOfInheritanceGroup &&
      options.inheritanceGroups &&
      !options.inheritanceGroups.includes(
        inheritanceMetadata.parentOfInheritanceGroup
      )
    ) {
      return false;
    }

    if (inheritanceMetadata.parentOfInheritanceGroup !== undefined) {
      this.parentOfInheritanceGroup =
        inheritanceMetadata.parentOfInheritanceGroup;
    }
    if (inheritanceMetadata.excludeFromInheritance !== undefined) {
      this.excludeFromInheritance = inheritanceMetadata.excludeFromInheritance;
    }
    return true;
  }
}
